/**
 * TSF (Two-Stage Filtering) Geocoding Types
 * 
 * Sistema de geocodificación en dos fases:
 * 1. Filtrado por código INE municipal
 * 2. Búsqueda fuzzy entre candidatos del municipio
 * 
 * Rendimiento validado TSF-01:
 * - SINGLETON: 40% resolución directa
 * - FUZZY: 7.5% resolución adicional
 * - Total: 47.5% (objetivo era 12%)
 * 
 * @version 1.0.0
 * @date 2025-01-08
 * @see docs/implementacion/TSF-01_INFORME_VALIDACION.md
 */

import type { InfrastructureType } from '../InfrastructureClassifier';
import type { LocalFeature, InfrastructureCategory } from '../LocalDataService';

// ============================================================================
// ESTRATEGIAS TSF
// ============================================================================

/**
 * Estrategias de resolución TSF.
 * 
 * - SINGLETON: Único candidato en el municipio → resolución directa
 * - FUZZY: Múltiples candidatos → búsqueda por similitud de nombre
 * - DISABLED: Bypass del sistema TSF (usar APIs directas)
 */
export type TSFStrategy = 'SINGLETON' | 'FUZZY' | 'DISABLED';

/**
 * Estados de resultado de búsqueda TSF.
 * 
 * - SINGLETON: Resuelto por candidato único
 * - FUZZY_BEST: Resuelto por mejor match fuzzy
 * - TOO_MANY_CANDIDATES: Demasiados candidatos, fuzzy no concluyente
 * - NOT_RESOLVED: No se encontró match suficiente
 * - NO_LOCAL_DATA: Sin datos locales para el municipio/tipo
 */
export type TSFResolutionStatus = 
  | 'SINGLETON' 
  | 'FUZZY_BEST' 
  | 'TOO_MANY_CANDIDATES' 
  | 'NOT_RESOLVED' 
  | 'NO_LOCAL_DATA';

// ============================================================================
// CONFIGURACIÓN
// ============================================================================

/**
 * Configuración del sistema TSF.
 * Valores por defecto optimizados según validación TSF-01.
 */
export interface TSFConfig {
  /**
   * Máximo de candidatos para intentar fuzzy matching.
   * Si hay más, se marca como TOO_MANY_CANDIDATES.
   * @default 8
   */
  maxFuzzyCandidates: number;

  /**
   * Score mínimo de fuzzy para aceptar un match.
   * Rango 0-1, donde 1 es match exacto.
   * @default 0.6
   */
  minFuzzyScore: number;

  /**
   * Habilita validación cruzada entre municipios colindantes.
   * Útil para infraestructuras en límites municipales.
   * @default false
   */
  enableCrossMunicipioValidation: boolean;

  /**
   * Categorías de infraestructura a incluir en búsqueda.
   * @default ['health', 'education', 'security', 'municipal', 'emergency', 'energy']
   */
  enabledCategories: InfrastructureCategory[];

  /**
   * Tiempo máximo de búsqueda en ms antes de timeout.
   * @default 5000
   */
  timeoutMs: number;
}

/**
 * Configuración por defecto para TSF.
 * Valores calibrados según resultados de TSF-01.
 */
export const DEFAULT_TSF_CONFIG: Readonly<TSFConfig> = {
  maxFuzzyCandidates: 8,
  minFuzzyScore: 0.6,
  enableCrossMunicipioValidation: false,
  enabledCategories: ['health', 'education', 'security', 'municipal', 'emergency', 'energy'],
  timeoutMs: 5000,
} as const;

// ============================================================================
// RESULTADOS
// ============================================================================

/**
 * Resultado de una búsqueda TSF.
 */
export interface TSFResult {
  /**
   * Estrategia que produjo el resultado.
   */
  status: TSFResolutionStatus;

  /**
   * Indica si se resolvió exitosamente.
   * true si status es SINGLETON o FUZZY_BEST.
   */
  resolved: boolean;

  /**
   * Feature candidato encontrado (null si no resuelto).
   */
  candidate: LocalFeature | null;

  /**
   * Número de candidatos encontrados en el municipio.
   */
  candidateCount: number;

  /**
   * Score de similitud fuzzy (solo si status = FUZZY_BEST).
   * Rango 0-1.
   */
  fuzzyScore?: number;

  /**
   * Confianza del resultado (0-100).
   * - SINGLETON: 95-100
   * - FUZZY_BEST con score > 0.8: 80-94
   * - FUZZY_BEST con score 0.6-0.8: 60-79
   */
  confidence: number;
}

// ============================================================================
// REQUEST/RESPONSE
// ============================================================================

/**
 * Solicitud de geocodificación TSF.
 */
export interface TSFGeocodingRequest {
  /**
   * Nombre de la infraestructura a geocodificar.
   * Se normaliza internamente (mayúsculas, sin acentos).
   */
  nombre: string;

  /**
   * Tipo de infraestructura para filtrar búsqueda.
   */
  tipo: InfrastructureType;

  /**
   * Código INE del municipio (5 dígitos).
   * Formato: PPMMM (provincia + municipio).
   * @example "18087" para Granada capital
   */
  codMunicipio: string;

  /**
   * Estrategia a usar (opcional).
   * Si no se especifica, se usa auto-selección.
   * @default undefined (auto)
   */
  estrategia?: TSFStrategy;

  /**
   * Configuración personalizada (opcional).
   * Se fusiona con DEFAULT_TSF_CONFIG.
   */
  config?: Partial<TSFConfig>;
}

/**
 * Respuesta de geocodificación TSF.
 * Extiende TSFResult con coordenadas y metadatos.
 */
export interface TSFGeocodingResponse extends TSFResult {
  /**
   * Coordenadas EPSG:25830 si se resolvió.
   * Compatible con QGIS directamente.
   */
  coordenadas?: {
    /** Coordenada X (Este) en metros */
    x: number;
    /** Coordenada Y (Norte) en metros */
    y: number;
  };

  /**
   * Nombre normalizado del candidato encontrado.
   */
  nombreNormalizado?: string;

  /**
   * Categoría de infraestructura del candidato.
   */
  categoria?: InfrastructureCategory;

  /**
   * Tiempo de ejecución en milisegundos.
   */
  tiempoMs: number;

  /**
   * Código INE del municipio usado en la búsqueda.
   */
  codMunicipioBuscado: string;
}

// ============================================================================
// ESTADÍSTICAS
// ============================================================================

/**
 * Estadísticas agregadas de resolución TSF.
 * Útil para monitoreo y optimización.
 */
export interface TSFStats {
  /** Total de solicitudes procesadas */
  totalRequests: number;
  
  /** Solicitudes resueltas exitosamente */
  resolved: number;
  
  /** Resoluciones por SINGLETON */
  singletonHits: number;
  
  /** Resoluciones por FUZZY_BEST */
  fuzzyHits: number;
  
  /** No resueltos por TOO_MANY_CANDIDATES */
  tooManyCandidates: number;
  
  /** No resueltos por NOT_RESOLVED */
  notResolved: number;
  
  /** Sin datos locales */
  noLocalData: number;
  
  /** Tiempo promedio de resolución (ms) */
  avgTimeMs: number;
  
  /** Tasa de resolución (0-100) */
  resolutionRate: number;
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Verifica si un status indica resolución exitosa.
 */
export function isResolvedStatus(status: TSFResolutionStatus): boolean {
  return status === 'SINGLETON' || status === 'FUZZY_BEST';
}

/**
 * Verifica si un resultado TSF está resuelto.
 */
export function isTSFResolved(result: TSFResult): result is TSFResult & { candidate: LocalFeature } {
  return result.resolved && result.candidate !== null;
}

/**
 * Calcula confianza basada en status y fuzzyScore.
 */
export function calculateConfidence(status: TSFResolutionStatus, fuzzyScore?: number): number {
  switch (status) {
    case 'SINGLETON':
      return 98;
    case 'FUZZY_BEST':
      if (fuzzyScore === undefined) return 70;
      if (fuzzyScore >= 0.9) return 90;
      if (fuzzyScore >= 0.8) return 85;
      if (fuzzyScore >= 0.7) return 75;
      return 65;
    case 'TOO_MANY_CANDIDATES':
    case 'NOT_RESOLVED':
    case 'NO_LOCAL_DATA':
      return 0;
  }
}
